// CSS
import "../../../styles/global.scss";

import React, { useState } from 'react';
import PropTypes from 'prop-types';

import DataFeed from '../../data-feed/jsx/DataFeed.jsx';
import MapView from '../../map-view/jsx/MapView.jsx';
import ListView from '../../list-view/jsx/ListView.jsx';
import AlertBox from '../../alert-box/jsx/AlertBox.jsx';
import LocationInput from '../../location-input/jsx/LocationInput.jsx';
import SearchFilters from '../../search-filters/jsx/SearchFilters.jsx';
import SearchMessage from '../../search-message/jsx/SearchMessage.jsx';
import ConcentrationsCTA from '../../concentrations-cta/jsx/ConcentrationsCTA.jsx';
import LoadingOverlay from '../../loading-overlay/jsx/LoadingOverlay.jsx';
import PollutantPopup from '../../pollutant-popup/jsx/PollutantPopup.jsx';

/**
 * Air Quality Monitoring Stations Map application
 *
 * @param {Object} appConfig The required application configuration. See below for required properties and examples.
 *
  {
    "mapCopy": "<p>Your HTML here.</p>", // HTML String
    "asideTitle": "Air quality concentration data", // String
    "asideDescription": "This data shows stations in <strong>NSW.</strong>", // HTML String
    "asideLinkLabel": "View NSW data", // String
    "asideLink": "https://www.airquality.nsw.gov.au/", // URL String
    "goodStatusIcon": "./status-good.svg", // Image URL String
    "goodStatusIconExcluded": "./status-good-excluded.svg", // Image URL String
    "fairStatusIcon": "./status-fair.svg", // Image URL String
    "fairStatusIconExcluded": "./status-fair-excluded.svg", // Image URL String
    "poorStatusIcon": "./status-poor.svg", // Image URL String
    "poorStatusIconExcluded": "./status-poor-excluded.svg", // Image URL String
    "veryPoorStatusIcon": "./status-very-poor.svg", // Image URL String
    "veryPoorStatusIconExcluded": "./status-very-poor-excluded.svg", // Image URL String
    "extremelyPoorStatusIcon": "./status-extremely-poor.svg", // Image URL String
    "extremelyPoorStatusIconExcluded": "./status-extremely-poor-excluded.svg", // Image URL String
    "inactiveStatusIcon": "./status-inactive.svg", // Image URL String
    "inactiveStatusIconExcluded": "./status-inactive-excluded.svg", // Image URL String
    "satelliteMapIcon": "./externals/nsw-satellite.jpg", // Image URL String
    "roadMapIcon": "./externals/nsw-road.jpg", // Image URL String
    "pollutantPopupPM25": "./pollutant-lightboxes/pm2.5?SQ_ASSET_CONTENTS_RAW", // Endpoint URL String
    "pollutantPopupPM10": "./pollutant-lightboxes/pm10?SQ_ASSET_CONTENTS_RAW", // Endpoint URL String
    "pollutantPopupOzone": "./pollutant-lightboxes/Ozone?SQ_ASSET_CONTENTS_RAW", // Endpoint URL String
    "staticStationData": "__data/assets/text_file/0006/596760/Observations_Text_File.txt", // Endpoint URL string
    "latestStaticHour": "12", // Number as string from Matrix metadata
    "latestStaticDate": "2024-01-17" // Date as string from Matrix metadata,
    "stationDataFeed": "./map-data/aq-map-data-feed", // Endpoint URL String
    "googlePlacesApiEndpoint": "./google/google-places-api-geocode", // Endpoint URL String
    "googlePlaceDetailsApiEndpoint": "./google/google-place-details-api", // Endpoint URL String
    "googleGeocodingApiEndpoint": "./google/google-geocoding-api", // Endpoint URL String
    "googleMapsApikey": "AIzaSyDbeQ8isUCieZ7dSwKI28IXp7wkFSZ7B_w" // String
  };
 *
 */
const App = ({ appConfig }) => {
  const [ visibleTab, setVisibleTab ] = useState("map");

  /**
   * Respond to clicks on the "map type" buttons.
   * Update the map to display a different type of map (e.g. roadmap vs satellite)
   *
   * @param {Click Event} evt Event that triggered the click
   *
   * @returns void
   */
  const onViewButtonClick = (evt) => {
    const viewType = evt.target.getAttribute('data-view');

    evt.preventDefault();

    setVisibleTab(viewType);
  };

  return (
    <section className="station-map-app__container">
      <DataFeed />

      <div className="station-map-app__header">
        <div id="search-filters" className="station-map-search-filters__container">
          <p className="station-map-intro-message">
            Search by suburb or location.
          </p>
          <LocationInput locationConfig={
            {
                "googlePlacesApiEndpoint": appConfig.googlePlacesApiEndpoint,
                "googlePlaceDetailsApiEndpoint": appConfig.googlePlaceDetailsApiEndpoint,
                "googleGeocodingApiEndpoint": appConfig.googleGeocodingApiEndpoint
              }
            }
          />
          <SearchMessage />

          <SearchFilters
            stationEndpointUrl={appConfig.stationDataFeed}
            staticDataUrl={appConfig.staticStationData}
            staticHour={appConfig.latestStaticHour}
            staticDate={appConfig.latestStaticDate}
          />

          <div className="station-map-skip-links" aria-hidden="true">
            <nav className="station-map-skip-links__list">
              <a className="station-map-skip-links__link" href="#wind-toggle">Skip interactive Google map</a>
            </nav>
          </div>
        </div>

        <ConcentrationsCTA
          ctaConfig={
            {
              "title": appConfig.asideTitle,
              "description": appConfig.asideDescription,
              "linkLabel": appConfig.asideLinkLabel,
              "link": appConfig.asideLink
            }
          }
        />
      </div>

      <div className="station-map-tab-buttons__container">
        <button onClick={onViewButtonClick} data-view="map" className={(visibleTab === "map") ? 'is-active station-map-tab-button' : 'station-map-tab-button'}>
          Map view
        </button>
        <button onClick={onViewButtonClick} data-view="list" className={(visibleTab === "list") ? 'is-active station-map-tab-button' : 'station-map-tab-button'}>
          List view
        </button>
      </div>

      <ListView
        isHidden={(visibleTab === "list") ? false : true}
        textContent={appConfig.mapCopy}
      />

      <MapView
        mapConfig={
          {
            "textContent": appConfig.mapCopy,
            "mapsApiKey": appConfig.googleMapsApikey,
            "mapIcons": {
              "good": appConfig.goodStatusIcon,
              "goodExcluded": appConfig.goodStatusIconExcluded,
              "fair": appConfig.fairStatusIcon,
              "fairExcluded": appConfig.fairStatusIconExcluded,
              "poor": appConfig.poorStatusIcon,
              "poorExcluded": appConfig.poorStatusIconExcluded,
              "veryPoor": appConfig.veryPoorStatusIcon,
              "veryPoorExcluded": appConfig.veryPoorStatusIconExcluded,
              "extremelyPoor": appConfig.extremelyPoorStatusIcon,
              "extremelyPoorExcluded": appConfig.extremelyPoorStatusIconExcluded,
              "inactive": appConfig.inactiveStatusIcon,
              "inactiveExcluded": appConfig.inactiveStatusIconExcluded,
              "satellite": appConfig.satelliteMapIcon,
              "road": appConfig.roadMapIcon
            }
          }
        }
        isHidden={(visibleTab === "map") ? false : true}
      />

      <ConcentrationsCTA
        ctaConfig={
            {
              "title": appConfig.asideTitle,
              "description": appConfig.asideDescription,
              "linkLabel": appConfig.asideLinkLabel,
              "link": appConfig.asideLink
            }
          }
      />

      <LoadingOverlay />
      <PollutantPopup
        popupConfig={
          {
            "pm25Url": appConfig.pollutantPopupPM25,
            "pm10Url": appConfig.pollutantPopupPM10,
            "ozoneUrl": appConfig.pollutantPopupOzone
          }
        }
      />
      <AlertBox />
    </section>
  );
};

App.propTypes = {
  "appConfig": PropTypes.object
};

export default App;
